/**
 * Cliente das callables de Clubes e Desafios.
 *
 * O CLIENTE NUNCA DECIDE. Quem valida limite, cooldown de recusa, visibilidade
 * e permissão de admin é o backend — estas funções só transportam e devolvem o
 * veredito. O mesmo contrato do `competicaoApi.js` da verificação.
 *
 * As rules negam qualquer escrita na árvore `CLUBES`, então não existe um
 * segundo caminho: ou passa por aqui, ou não acontece.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';

/** Chama uma callable e desembrulha o envelope `{success, ...}`. */
async function chamar(nome, dados = {}) {
  const fn = httpsCallable(functions, nome);
  const r = await fn(dados);
  return r?.data || {};
}

/**
 * Erro de callable traduzido para o que a UI precisa saber.
 *
 * O `details` que o backend manda junto (`{codigo, max, liberaEm}`) é o que
 * separa "não deu" de "você já participa de 5" — e é ele que a BottomSheet de
 * limite usa para escrever o número certo sem hardcode.
 */
export function detalhesDoErro(erro) {
  const d = erro?.details || {};
  return {
    codigo: d.codigo || erro?.code || 'DESCONHECIDO',
    mensagem: erro?.message || '',
    max: d.max ?? null,
    liberaEm: d.liberaEm ?? null,
  };
}

// ── Leitura ────────────────────────────────────────────────────────────────

/**
 * Página do Explorar. `cursor` é o id do último card da página anterior.
 * Sem `onSnapshot`: lista de descoberta que escuta em tempo real cobra por
 * cada card que qualquer pessoa no mundo editar.
 */
export const listarClubes = (p) => chamar('listarClubes', p);

/** Página do clube, antes ou depois de entrar. `periodoId` abre um mês antigo. */
export const getClube = (p) => chamar('getClube', p);

/** Minha posição quando estou fora do TOP — duas agregações no servidor. */
export const minhaPosicaoClube = (p) => chamar('minhaPosicaoClube', p);

/** Fila de solicitações do admin, com o contexto de cada pedido. */
export const listarSolicitacoes = (p) => chamar('listarSolicitacoes', p);

/** Meus clubes e desafios, para a aba Início. */
export const listarMeusClubes = () => chamar('listarMeusClubes', {});

/** Membros ativos (só o admin), para escolher quem assume ao sair. */
export const listarMembrosClube = (p) => chamar('listarMembrosClube', p);

/**
 * Prévia das recompensas na criação. Calculadora pura no servidor — nenhuma
 * escrita, nenhuma leitura além da config.
 *
 * O cliente chama a cada mudança de data ou de número de times, com debounce
 * de 400 ms (ver `useSimulacaoDeRecompensas`).
 */
export const simularRecompensas = (p) => chamar('simularRecompensas', p);

// ── Escrita ────────────────────────────────────────────────────────────────

export const criarClube = (p) => chamar('criarClube', p);
export const entrarClube = (p) => chamar('entrarClube', p);
export const pedirEntrada = (p) => chamar('pedirEntrada', p);
export const responderSolicitacao = (p) => chamar('responderSolicitacao', p);
export const moverMembro = (p) => chamar('moverMembro', p);
export const removerMembro = (p) => chamar('removerMembro', p);
export const sairClube = (p) => chamar('sairClube', p);
/** O admin passa o clube para outro membro ativo e sai. */
export const transferirAdminESair = (p) => chamar('transferirAdminESair', p);
export const editarClube = (p) => chamar('editarClube', p);
export const excluirClube = (p) => chamar('excluirClube', p);
